/**
 * partialBatchOutcome — pure decision for the batch-enrich "partial" response.
 *
 * The /batch-enrich-async route returns mode:'partial' when SOME chunks produced a
 * job while the rest were either semantic-gap BLOCKED (blocked_count > 0), already
 * processing (waiting_on_overlap) or already enriched (none) — the latter two carry
 * blocked_count === 0. The toast copy and the product-selection update must reflect
 * WHICH of those happened. Extracted as a pure function so the branching is unit-tested
 * (the handler in TheOffering.product.tsx just applies the returned decision).
 */

export interface PartialBatchResult {
  batches?: number | null;
  /** Cache-skipped by the backend before queuing — must be named, or the toast overstates. */
  skipped?: number;
  blocked_count?: number;
  blocked_product_ids?: unknown[];
  blocked_by_category?: Record<string, unknown> | null;
  // #3954 CR: a 207 mode:'partial' also fires when some chunks genuinely FAILED to
  // submit (failed_chunks > 0) — those products are not queued and not blocked.
  failed_chunks?: number;
  // #4367 iter-3: overlap-DEFERRED products — the job was created for the rest, but these ids
  // already sit in an active batch and were (correctly) not re-queued. They ARE retry targets
  // (retry once the active batch completes), so they must NOT be dropped from the selection.
  deferred_count?: number;
  deferred_product_ids?: unknown[];
  message?: string | null;
}

export type SelectionAction =
  | { action: 'narrow'; ids: string[] } // replace selection with exactly these ids
  | { action: 'clear' }                 // nothing left to act on → clear selection
  | { action: 'keep' };                 // can't determine ids → leave selection as-is

export interface PartialBatchOutcome {
  title: string;
  description: string;
  variant?: 'destructive';
  selection: SelectionAction;
}

export function computePartialBatchOutcome(result: PartialBatchResult): PartialBatchOutcome {
  const submitted = result.batches != null ? `${result.batches} batch job(s)` : 'batch job';
  // ⛔ The same cache-skip the sync shape carries. A PARTIAL response already tells the
  // operator some products did not enter a job; without this it attributes ALL of that to
  // blocks/failures/deferrals and never mentions the ones deliberately skipped as cached.
  const skippedNote = (result.skipped ?? 0) > 0
    ? ` ${result.skipped} already enriched and skipped.`
    : '';
  const blockedCount = result.blocked_count ?? 0;
  const failedChunks = result.failed_chunks ?? 0;
  const deferredCount = result.deferred_count ?? 0;
  const blockedIds = Array.isArray(result.blocked_product_ids)
    ? result.blocked_product_ids.map((id) => String(id))
    : [];
  const deferredIds = Array.isArray(result.deferred_product_ids)
    ? result.deferred_product_ids.map((id) => String(id))
    : [];

  // #3954 CR: a 207 partial can carry genuinely FAILED chunks (failed_chunks > 0). Those
  // products were NOT submitted and are NOT in blocked_product_ids — narrowing OR clearing
  // the selection would drop them. Handle failures FIRST and always KEEP the selection so
  // the operator can retry (also covers the blocked+failed combo). Only fall through to
  // narrow/clear when nothing failed.
  if (failedChunks > 0) {
    const blockNote = blockedCount > 0 ? ` ${blockedCount} blocked by unresolved semantic gaps.` : '';
    return {
      title: 'Partially Queued',
      variant: 'destructive',
      description: `${submitted} submitted; ${failedChunks} chunk(s) failed to submit.${blockNote} Your selection is unchanged — retry.` + skippedNote,
      selection: { action: 'keep' },
    };
  }

  // Retryable remainder: semantic-gap BLOCKED and/or overlap-DEFERRED products. Both are retry
  // targets (blocked → resolve the gaps then retry; deferred → retry once the active batch completes),
  // so the selection must narrow to their UNION rather than clear. #4367 iter-3 BLOCK 2: before this,
  // deferred-only responses (the single-batch overlap path — 100% of the TradeInn wave) had
  // blocked_count === 0 and fell through to `clear`, dropping the deferred retry targets while the
  // toast told the operator to retry. Only narrow when we have the FULL id set for every non-zero
  // count (mirrors the blocked-set rule); otherwise KEEP so no retry target is silently dropped.
  if (blockedCount > 0 || deferredCount > 0) {
    const cats = result.blocked_by_category ? Object.keys(result.blocked_by_category) : [];
    const parts: string[] = [];
    if (blockedCount > 0) {
      parts.push(`${blockedCount} product(s) blocked by unresolved semantic gaps${cats.length ? ` (${cats.join(', ')})` : ''}`);
    }
    if (deferredCount > 0) {
      parts.push(`${deferredCount} product(s) deferred — already in an active batch, retry once it completes`);
    }
    const detail = parts.join('; ');
    // Blocked is an actionable error (destructive); a deferred-only remainder is benign (informational).
    const variant = blockedCount > 0 ? ('destructive' as const) : undefined;

    const haveAllBlocked = blockedCount === 0 || blockedIds.length >= blockedCount;
    const haveAllDeferred = deferredCount === 0 || deferredIds.length >= deferredCount;

    if (haveAllBlocked && haveAllDeferred) {
      const ids = [...new Set([...blockedIds, ...deferredIds])];
      return {
        title: 'Partially Queued',
        ...(variant && { variant }),
        description: `${submitted} submitted; ${detail}. Selection narrowed to the retry targets.` + skippedNote,
        selection: { action: 'narrow', ids },
      };
    }

    // The backend did not return the full id set for some non-zero count — narrowing would drop
    // retry targets. KEEP the current selection (don't silently clear) and don't claim "narrowed".
    return {
      title: 'Partially Queued',
      ...(variant && { variant }),
      description: `${submitted} submitted; ${detail}. The full retry set wasn't returned, so your selection is unchanged — retry.` + skippedNote,
      selection: { action: 'keep' },
    };
  }

  // blocked_count === 0 AND deferred_count === 0 AND failed_chunks === 0 — the remainder was already
  // processing (waiting_on_overlap) or already enriched (none). Nothing to retry; clear the selection
  // so already-queued/processing/enriched products are never left selected for an accidental re-submit.
  return {
    title: 'Partially Queued',
    // ⛔ THE SKIP MATTERS MOST ON THIS BRANCH, because it is the one that CLEARS the selection.
    // A response carrying submitted work, an overlap/none remainder AND route-level cache-skips
    // would wipe the operator's selection while accounting for everything except the skips —
    // precisely the silent outcome this change exists to remove. It was missed because the
    // description here spans two lines and the edit that fixed its siblings was a one-line regex.
    description:
      (result.message ||
        `${submitted} submitted; the remaining selected products were already processing or already enriched — nothing to retry.`)
      + skippedNote,
    selection: { action: 'clear' },
  };
}

/**
 * The full-block sibling of computePartialBatchOutcome. When the /batch-enrich-async route
 * creates NO job at all it returns HTTP 409 { success:false } — either every chunk was
 * semantic-gap BLOCKED (mode:'blocked') or a mix of blocked chunks AND genuinely failed
 * chunks (mode:'blocked_and_failed', backend product-enrichment.js:4298). Both are primarily
 * an actionable semantic-gap block (resolve the gaps, then retry), so both get the
 * 'Enrichment Blocked' title — the backend `message` already narrates any chunk failures.
 * Only a non-blocked error falls through to the generic 'Batch Job Failed'.
 *
 * #3962 CR2 MINOR: the handler previously branched on mode === 'blocked' only, so
 * 'blocked_and_failed' fell through to 'Batch Job Failed', hiding the semantic-gap reason.
 * Extracted as a pure function so the mode branching is unit-tested.
 */
export interface BlockedBatchError {
  mode?: string;
  message?: string | null;
  error?: string | null;
}

export interface BlockedBatchOutcome {
  title: string;
  description: string;
  variant: 'destructive';
}

export function computeBlockedOutcome(error: BlockedBatchError): BlockedBatchOutcome {
  const blocked = error?.mode === 'blocked' || error?.mode === 'blocked_and_failed';
  return {
    title: blocked ? 'Enrichment Blocked' : 'Batch Job Failed',
    description: error?.message || error?.error || 'Failed to start batch enrichment',
    variant: 'destructive',
  };
}

/**
 * syncEnrichOutcome — pure decision for the batch-enrich `mode:'sync'` response (S1,
 * task-8ahq0c6onh).
 *
 * ⛔⛔ WHY THIS EXISTS AT ALL. The admin used to fork on a CLIENT-SIDE threshold of 20:
 * larger selections went to /batch-enrich-async (which honours `forceReEnrich`), smaller
 * ones looped POST /enrich/:productId with a body of `{ siteId }` alone. That route reads
 * no force option and ProductEnrichmentService.enrichProduct has no cache check and no
 * already-enriched early return, so BELOW THE THRESHOLD THE CHECKBOX WAS INERT IN BOTH
 * DIRECTIONS: ticking Force changed nothing because the path already re-enriched
 * everything, and LEAVING IT OFF ALSO CHANGED NOTHING — the operator paid for LLM
 * re-enrichment they had explicitly declined.
 *
 * 🔑 THE ROUTE ALREADY MAKES THIS DECISION, AND MAKES IT BETTER. /batch-enrich-async forks
 * internally on the same count, keeps IMMEDIATE results for small selections, and honours
 * `forceReEnrich` on BOTH sides of its own fork. The client fork was a second, worse copy
 * of a decision that already existed — so it is deleted rather than taught the same lesson.
 * A sequence of branches makes the answer depend on which branch ran; the fix is to remove
 * the decision, not to improve it.
 *
 * The `mode:'sync'` shape it returns carries PER-PRODUCT results — `status` of
 * enriched | blocked | error, plus `message`/`blockingGaps`/`gateError` on the blocked ones.
 * The old loop threw all of that away, collapsing every response to `{ success: boolean }`,
 * so a semantic-gap block and a 500 were indistinguishable to the operator. It is surfaced
 * here instead.
 */
export interface SyncEnrichResult {
  mode?: string;
  /** Products the backend cache-skipped because they were already enriched and Force was off. */
  skipped?: number;
  enriched?: number;
  blocked?: number;
  failed?: number;
  total?: number;
  results?: Array<{
    id?: string;
    status?: string;
    message?: string | null;
    blockingGaps?: unknown;
    gateError?: string | null;
  }>;
  message?: string | null;
}

export interface SyncEnrichOutcome {
  title: string;
  description: string;
  variant?: 'destructive';
  selection: SelectionAction;
  /** true when the caller should refresh the product list — i.e. something actually changed. */
  refresh: boolean;
}

export function computeSyncEnrichOutcome(result: SyncEnrichResult): SyncEnrichOutcome {
  const enriched = result.enriched ?? 0;
  const blocked = result.blocked ?? 0;
  const failed = result.failed ?? 0;
  const total = result.total ?? enriched + blocked + failed;
  const rows = Array.isArray(result.results) ? result.results : [];
  const skipped = result.skipped ?? 0;

  // ⚠️ ATTEMPTED ZERO IS NOT A FAILURE, and it is the case the old UI could not express at
  // all. With Force OFF the route filters already-enriched products out before running, so
  // an operator who selects 3 enriched products and does not tick Force gets total:0 —
  // which is the cache-skip WORKING. Reporting it as "0 enriched, 0 errors" reads as a
  // broken button and is what sends someone to tick Force and pay for the re-run.
  if (total === 0) {
    return {
      title: 'Nothing to Enrich',
      description:
        result.message
        || 'Every selected product is already enriched, so nothing was re-run. Tick "Force re-enrichment" to run them again.',
      selection: { action: 'keep' },
      refresh: false,
    };
  }

  // The reason, not just the count. `blockingGaps` is deliberately NOT rendered here — it is
  // a structure, and a toast that prints one is unreadable; the per-product message already
  // names the category. The first distinct reason is shown because repeating the same gap
  // message once per product buries it.
  const reasons = Array.from(
    new Set(
      rows
        .filter((r) => r.status === 'blocked')
        .map((r) => r.message || r.gateError)
        .filter((m): m is string => typeof m === 'string' && m.trim() !== '')
    )
  );
  const errorNote = failed > 0 ? ` ${failed} failed.` : '';
  const blockedNote = blocked > 0
    ? ` ${blocked} blocked by unresolved semantic gaps${reasons.length ? `: ${reasons[0]}` : ''}.`
    : '';

  if (enriched === 0) {
    // ⚠️ KEEP the selection. Nothing succeeded, so every id is still a retry target — and
    // for the blocked ones the operator's next step (resolve the gaps, then retry this exact
    // set) needs the set intact.
    return {
      title: blocked > 0 ? 'Enrichment Blocked' : 'Enrichment Failed',
      description: `${total} product(s) attempted, none enriched.${blockedNote}${errorNote}${skipped > 0 ? ` ${skipped} already enriched and skipped.` : ''}`,
      variant: 'destructive',
      selection: { action: 'keep' },
      refresh: false,
    };
  }

  if (blocked > 0 || failed > 0) {
    // Partial success: narrow the selection to what did NOT get through, so a retry after
    // resolving the gaps targets exactly the remainder rather than re-billing the successes.
    const unresolved = rows
      .filter((r) => r.status !== 'enriched' && typeof r.id === 'string')
      .map((r) => String(r.id));
    return {
      title: 'Partially Enriched',
      description: `Enriched ${enriched}/${total}.${blockedNote}${errorNote}${skipped > 0 ? ` ${skipped} already enriched and skipped.` : ''}`,
      variant: 'destructive',
      selection: unresolved.length > 0 ? { action: 'narrow', ids: unresolved } : { action: 'keep' },
      refresh: true,
    };
  }

  // ⚠️ THE SKIP HAS TO APPEAR IN THE COPY (codex @3969afb2b). The backend now narrows the set
  // BEFORE running, so `total` counts what it ATTEMPTED, not what the operator selected. Without
  // this, picking 5 products of which 3 were cached reports a confident "Enriched 2/2" and clears
  // all five — the operator is told the whole selection succeeded when most of it never ran. That
  // is the same overstating-what-happened defect this change exists to remove, arriving through
  // the copy instead of the code.
  const skippedNote = skipped > 0
    ? ` ${skipped} already enriched and skipped — tick "Force re-enrichment" to re-run them.`
    : '';

  return {
    title: 'Enrichment Complete',
    description: `Enriched ${enriched}/${total} product(s).${skippedNote}`,
    selection: { action: 'clear' },
    refresh: true,
  };
}
